import * as React from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Form,
	FormField,
	FormInput,
	Grid,
	GridColumn,
	GridRow,
	Icon,
	Segment
} from "semantic-ui-react";
import {Subbar, Title, PageHeader} from "ah-tools";
import {translate} from "react-i18next";
import {FormLabel, IconButton} from "../extensions";
import {MenuItem, SelectField} from "material-ui";
import isArray from 'lodash/isArray';
import {makeRoute, ROUTE_KFZ_EDIT} from "../routes";

const fuelTypes = [
	'Benzin',
	'Diesel',
	'Elektro',
	'Gas',
	'Wankel',
	'Alkohol',
	'Zweitakt'
];

class KfzModelEdit extends React.Component {
	static propTypes = {
		model: PropTypes.shape({
			kfz_id: PropTypes.number.isRequired,
			type: PropTypes.string.isRequired,
			hsn: PropTypes.string.isRequired,
			tsn: PropTypes.string.isRequired,
			ps: PropTypes.number.isRequired,
			kw: PropTypes.number.isRequired,
			ccm: PropTypes.number.isRequired,
			fuel: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
		}),
		push: PropTypes.func.isRequired,
		kfz: PropTypes.shape({
			kfz_id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired
		}).isRequired,
		isSaving: PropTypes.bool,
		onSave: PropTypes.func.isRequired,
		onDelete: PropTypes.func
	};
	static defaultProps = {
		model: null,
		kfz: {
			kfz_id: 0,
			name: '...'
		},
		isSaving: false
	};
	
	state = {
		model: {
			type: '',
			kfz_id: null,
			hsn: '',
			tsn: '',
			kw: 0,
			ps: 0,
			ccm: 0,
			fuel: []
		},
		confirmDelete: false
	};
	
	_handlePropUpdate = model => {
		if ( !model ) return;
		model.fuel = isArray(model.fuel) ? model.fuel : model.fuel.split(', ');
		this.setState({model});
	};
	
	componentDidMount = () => {
		this._handlePropUpdate(this.props.model);
	};
	
	componentWillReceiveProps = ({model}) => {
		this._handlePropUpdate(model);
	};
	
	updateModel = (index, value) => this.setState(state => ({
		...state,
		model: {
			...state.model,
			[index]: value
		}
	}));
	
	isValid = () => {
		const {
			type,
			hsn,
			tsn,
			kw,
			ps,
			ccm,
			fuel,
			fromYear,
			tillYear
		} = this.state.model;
		return (
			type.trim().length &&
			hsn.trim().length &&
			tsn.trim().length &&
			Number(kw) > 0 &&
			Number(ps) > 0 && Number(ps) > Number(kw) &&
			Number(ccm) > 0 &&
			Number(fromYear) > 1799 &&
			(tillYear ? Number(tillYear) >= Number(fromYear) : true) &&
			fuel.length > 0
		);
	};
	
	refineData = () => {
		const model = this.state.model;
		model.kw = Number(model.kw);
		model.ps = Number(model.ps);
		model.ccm = Number(model.ccm);
		model.fromYear = Number(model.fromYear);
		model.tillYear = model.tillYear ? Number(model.tillYear) : null;
		return model;
	};
	
	render = () => {
		const {t, model: origin, push, kfz, onSave, isSaving, onDelete} = this.props;
		const {model, confirmDelete} = this.state;
		
		return (
			<div>
				<PageHeader align={Subbar.ALIGN_START}>
					<IconButton onClick={() => push(makeRoute(ROUTE_KFZ_EDIT, {kfz_id: kfz.kfz_id}))}><Icon name={'chevron left'} size={'large'}/></IconButton>
					<Title>{!origin ? `${kfz.name}: Neues Model anlegen` : `${kfz.name}: ${origin.type} bearbeiten`}</Title>
				</PageHeader>
				<Segment basic padded={'very'}>
					<Form onSubmit={() => onSave(kfz, this.refineData(model))} loading={isSaving}>
						<Grid stackable>
							<GridRow columns={4}>
								<GridColumn>
									<FormField>
										<FormInput label={'Modeltyp-Bezeichnung'} labelPosition={'left'} value={model.type} required
										           onChange={(_, data) => this.updateModel('type', data.value)}>
											<FormLabel>Typ</FormLabel>
											<input/>
										</FormInput>
									</FormField>
									<FormField>
										<SelectField fullWidth autoWidth multiple value={model.fuel} floatingLabelText={'Antriebsart (Sprit)'} onChange={(_, key, values) => this.updateModel('fuel', values)}>
											{fuelTypes.map(fuelName =>
												<MenuItem key={fuelName} insetChildren value={fuelName} primaryText={fuelName} checked={model.fuel.indexOf(fuelName) > -1}/>
											)}
										</SelectField>
									</FormField>
									<FormField>
										<Subbar align={Subbar.ALIGN_BETWEEN} valign={Subbar.VALIGN_CENTER}>
											<FormInput labelPosition={'left'} required label={'Baujahr von'} className={'baujahr'} type={'number'} value={model.fromYear} min={1800} onChange={(_, data) => this.updateModel('fromYear', data.value)}>
												<FormLabel>von</FormLabel>
												<input/>
											</FormInput>
											<FormInput labelPosition={'left'} label={'Baujahr bis (optional)'} className={'baujahr'} type={'number'} value={model.tillYear} onChange={(_, data) => this.updateModel('tillYear', data.value)}>
												<FormLabel>bis</FormLabel>
												<input/>
											</FormInput>
										</Subbar>
									</FormField>
								</GridColumn>
								<GridColumn>
									<FormField>
										<FormInput label={'HSN'} labelPosition={'left'} value={model.hsn} minLength={4} maxLength={4} required
										           onChange={(_, data) => this.updateModel('hsn', data.value)}>
											<FormLabel>HSN</FormLabel>
											<input/>
										</FormInput>
									</FormField>
									<FormField>
										<FormInput label={'TSN'} labelPosition={'left'} value={model.tsn} minLength={3} maxLength={3} required
										           onChange={(_, data) => this.updateModel('tsn', data.value)}>
											<FormLabel>TSN</FormLabel>
											<input/>
										</FormInput>
									</FormField>
								</GridColumn>
								<GridColumn>
									<FormField>
										<FormInput label={'Leistung in KW'} labelPosition={'left'} value={model.kw} type={'number'} min={0} required
										           onChange={(_, data) => this.updateModel('kw', data.value)}>
											<FormLabel>KW</FormLabel>
											<input/>
										</FormInput>
									</FormField>
									<FormField>
										<FormInput label={'Leistung in PS'} labelPosition={'left'} value={model.ps} type={'number'} min={0} required
										           onChange={(_, data) => this.updateModel('ps', data.value)}>
											<FormLabel>PS</FormLabel>
											<input/>
										</FormInput>
									</FormField>
									<FormField>
										<FormInput label={'Hubraum in CCM'} labelPosition={'left'} value={model.ccm} type={'number'} min={0} required
										           onChange={(_, data) => this.updateModel('ccm', data.value)} step={1}>
											<FormLabel>CCM</FormLabel>
											<input/>
										</FormInput>
									</FormField>
								</GridColumn>
							</GridRow>
							<GridRow>
								<GridColumn>
									{!confirmDelete ?
										<Subbar align={onDelete ? Subbar.ALIGN_BETWEEN : Subbar.ALIGN_END}>
											{onDelete && <Button type={'button'}  negative inverted onClick={() => this.setState({confirmDelete: true})}>{t('actions.delete')}</Button>}
											<Button disabled={!this.isValid()} type={'submit'} positive inverted>{t('actions.save')}</Button>
										</Subbar>
										:
										<Subbar align={Subbar.ALIGN_END}>
											<Button type={'button'}  style={{marginRight: 20}} onClick={() => this.setState({confirmDelete: false})}>{t('actions.no')}</Button>
											<Button type={'button'}  negative onClick={() => onDelete(origin.kfz_id, origin.hsn, origin.tsn)}>{t('actions.yes')}</Button>
										</Subbar>
									}
									
								</GridColumn>
							</GridRow>
						</Grid>
					</Form>
					
					{/*<pre>{JSON.stringify(this.state, null, 4)}</pre>*/}
				</Segment>
			</div>
		);
	}
}

export default translate()(KfzModelEdit);


// WEBPACK FOOTER //
// ./src/components/KfzModelEdit.js